import { useMemo, useRef, useState } from 'react';

import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Button, Popover, SxProps, Theme, Typography } from '@mui/material';

const STRINGS = appStrings.modules.satActBooster.datePicker;

type BrandDatePickerProps = Readonly<{
  id?: string;
  /** ISO `YYYY-MM-DD` or null when unset. */
  value: string | null;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  /** ISO lower bound (inclusive). */
  min?: string;
  /** ISO upper bound (inclusive). */
  max?: string;
  /** ISO dates to flag with a dot in the grid. */
  markedDates?: readonly string[];
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  /** Stretches the trigger to fill its container instead of the default 200px. */
  fullWidth?: boolean;
  /** Applied to the root, not the trigger. */
  sx?: SxProps<Theme>;
}>;

const pad = (value: number) => String(value).padStart(2, '0');

const toISO = (date: Date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parseISO = (iso: string) => {
  const [year, month, day] = iso.split('-').map(Number);

  return new Date(year, month - 1, day);
};

const formatDisplay = (iso: string) =>
  parseISO(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatMonthTitle = (year: number, month: number) =>
  new Date(year, month, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

export const BrandDatePicker = ({
  id,
  value,
  onChange,
  onBlur,
  min,
  max,
  markedDates = [],
  error = false,
  helperText,
  placeholder = STRINGS.placeholder,
  fullWidth = false,
  sx,
}: BrandDatePickerProps) => {
  // Ids are derived from the trigger's so every control in an unnamed picker still resolves.
  const idBase = id ?? 'brand-date-picker';
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState<string | null>(value);

  const todayISO = toISO(new Date());
  const anchor = value ? parseISO(value) : new Date();
  const [view, setView] = useState({ year: anchor.getFullYear(), month: anchor.getMonth() });

  const cells = useMemo(() => {
    const firstOfMonth = new Date(view.year, view.month, 1);
    const leading = (firstOfMonth.getDay() + 6) % 7; // shift so Monday is column 0
    const gridStart = new Date(view.year, view.month, 1 - leading);

    return Array.from(
      { length: 42 },
      (_, index) =>
        new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index),
    );
  }, [view]);

  const openPicker = () => {
    setPending(value);

    const base = value ? parseISO(value) : new Date();

    setView({ year: base.getFullYear(), month: base.getMonth() });
    setOpen(true);
  };

  const closePicker = () => setOpen(false);

  const shiftMonth = (delta: number) => {
    setView((current) => {
      const next = new Date(current.year, current.month + delta, 1);

      return { year: next.getFullYear(), month: next.getMonth() };
    });
  };

  const goToToday = () => {
    const now = new Date();

    setView({ year: now.getFullYear(), month: now.getMonth() });
  };

  const apply = () => {
    onChange(pending);
    closePicker();
    onBlur?.();
  };

  return (
    <Box sx={sx}>
      <Box
        component="button"
        type="button"
        id={id}
        ref={triggerRef}
        onClick={openPicker}
        sx={triggerSx(error, fullWidth)}
      >
        <AppIcon iconName="Calendar" diameter="20px" color="Colors/Foreground/fg-quinary" />
        <Typography variant="text-md-regular" sx={value ? triggerValueSx : triggerPlaceholderSx}>
          {value ? formatDisplay(value) : placeholder}
        </Typography>
      </Box>

      {error && helperText && (
        <Typography variant="text-sm-regular" sx={helperTextSx}>
          {helperText}
        </Typography>
      )}

      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={closePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: popoverPaperSx } }}
      >
        <Box sx={panelSx}>
          <Box sx={headerSx}>
            <Box
              component="button"
              type="button"
              id={`${idBase}-prev-month`}
              aria-label={STRINGS.previousMonth}
              onClick={() => shiftMonth(-1)}
              sx={navButtonSx}
            >
              <AppIcon iconName="ChevronLeft" diameter="20px" color="Colors/Text/text-secondary" />
            </Box>
            <Typography variant="text-md-semibold" sx={monthTitleSx}>
              {formatMonthTitle(view.year, view.month)}
            </Typography>
            <Box
              component="button"
              type="button"
              id={`${idBase}-next-month`}
              aria-label={STRINGS.nextMonth}
              onClick={() => shiftMonth(1)}
              sx={navButtonSx}
            >
              <AppIcon iconName="ChevronRight" diameter="20px" color="Colors/Text/text-secondary" />
            </Box>
          </Box>

          <Box sx={inputRowSx}>
            <Box sx={selectedFieldSx}>
              <Typography
                variant="text-md-regular"
                sx={pending ? triggerValueSx : triggerPlaceholderSx}
              >
                {pending ? formatDisplay(pending) : placeholder}
              </Typography>
            </Box>
            <Button
              id={`${idBase}-today`}
              variant="outlined"
              onClick={goToToday}
              sx={todayButtonSx}
            >
              {STRINGS.today}
            </Button>
          </Box>

          <Box sx={weekdayRowSx}>
            {STRINGS.weekdayInitials.map((label) => (
              <Typography key={label} variant="text-sm-medium" sx={weekdayLabelSx}>
                {label}
              </Typography>
            ))}
          </Box>

          <Box sx={gridSx}>
            {cells.map((date) => {
              const iso = toISO(date);
              const inMonth = date.getMonth() === view.month;
              const disabled = (min !== undefined && iso < min) || (max !== undefined && iso > max);
              const selected = pending === iso;
              const isToday = iso === todayISO;
              const marked = markedDates.includes(iso);

              return (
                <Box
                  key={iso}
                  component="button"
                  type="button"
                  id={`${idBase}-day-${iso}`}
                  disabled={disabled}
                  aria-label={formatDisplay(iso)}
                  aria-pressed={selected}
                  onClick={() => setPending(iso)}
                  sx={dayCellSx({ inMonth, selected, isToday, disabled })}
                >
                  {date.getDate()}
                  <Box component="span" sx={dayDotSx(marked && !selected)} />
                </Box>
              );
            })}
          </Box>

          <Box sx={footerSx}>
            <Button
              id={`${idBase}-cancel`}
              variant="outlined"
              onClick={closePicker}
              sx={cancelButtonSx}
            >
              {STRINGS.cancel}
            </Button>
            <Button id={`${idBase}-apply`} variant="contained" onClick={apply} sx={applyButtonSx}>
              {STRINGS.apply}
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

const triggerSx =
  (error: boolean, fullWidth: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: backrs.spacing['spacing-md'],
    width: fullWidth ? '100%' : '200px',
    maxWidth: '100%',
    padding: `10px ${backrs.spacing['spacing-lg']}`,
    borderRadius: backrs.radius['radius-md'],
    border: `1px solid ${
      error
        ? backrs.colors['Colors/Border/border-error']
        : backrs.colors['Colors/Border/border-primary']
    }`,
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: backrs.colors['Colors/Background/bg-primary-hover'],
    },
  });

const triggerValueSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const triggerPlaceholderSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-placeholder'],
});

const helperTextSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-xs'],
  color: backrs.colors['Colors/Text/text-error-primary'],
});

const popoverPaperSx: SxProps<Theme> = ({ backrs }) => ({
  width: '328px',
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  boxShadow: backrs.shadows['shadow-xl'],
  overflow: 'visible',
});

const panelSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  padding: backrs.spacing['spacing-xl'],
});

const headerSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const navButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: backrs.radius['radius-md'],
  border: 'none',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-primary-hover'],
  },
});

const monthTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const inputRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const selectedFieldSx: SxProps<Theme> = ({ backrs }) => ({
  flex: '1 0 0',
  display: 'flex',
  alignItems: 'center',
  padding: `10px ${backrs.spacing['spacing-lg']}`,
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const todayButtonSx: SxProps<Theme> = ({ backrs }) => ({
  flex: '0 0 auto',
  padding: `10px ${backrs.spacing['spacing-lg']}`,
  borderRadius: backrs.radius['radius-md'],
  borderColor: backrs.colors['Colors/Border/border-primary'],
  color: backrs.colors['Colors/Text/text-secondary'],
  textTransform: 'none',
});

const weekdayRowSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  justifyItems: 'center',
};

const weekdayLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const gridSx: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  justifyItems: 'center',
  rowGap: '4px',
};

const dayCellSx =
  ({
    inMonth,
    selected,
    isToday,
    disabled,
  }: {
    inMonth: boolean;
    selected: boolean;
    isToday: boolean;
    disabled: boolean;
  }): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'relative',
    width: '36px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: backrs.radius['radius-full'],
    border: 'none',
    fontSize: '14px',
    fontWeight: selected ? 600 : 400,
    cursor: disabled ? 'default' : 'pointer',
    backgroundColor: selected
      ? backrs.colors['Colors/Background/bg-brand-solid']
      : isToday
        ? backrs.colors['Colors/Background/bg-secondary']
        : 'transparent',
    color: selected
      ? backrs.colors['Colors/Text/text-white']
      : disabled
        ? backrs.colors['Colors/Text/text-quaternary']
        : inMonth
          ? backrs.colors['Colors/Text/text-primary']
          : backrs.colors['Colors/Text/text-tertiary'],
    opacity: disabled ? 0.5 : 1,
    '&:hover': {
      backgroundColor: selected
        ? backrs.colors['Colors/Background/bg-brand-solid']
        : disabled
          ? 'transparent'
          : backrs.colors['Colors/Background/bg-primary-hover'],
    },
  });

const dayDotSx =
  (visible: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'absolute',
    bottom: '4px',
    width: '4px',
    height: '4px',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: visible ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
  });

const footerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-md'],
  paddingTop: backrs.spacing['spacing-lg'],
  borderTop: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  '& > *': { flex: '1 0 0' },
});

const cancelButtonSx: SxProps<Theme> = ({ backrs }) => ({
  borderRadius: backrs.radius['radius-md'],
  borderColor: backrs.colors['Colors/Border/border-primary'],
  color: backrs.colors['Colors/Text/text-secondary'],
  textTransform: 'none',
});

const applyButtonSx: SxProps<Theme> = ({ backrs }) => ({
  borderRadius: backrs.radius['radius-md'],
  backgroundColor: backrs.colors['Colors/Background/bg-brand-solid'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  boxShadow: backrs.shadows['shadow-md'],
  textTransform: 'none',
  '&:hover': {
    backgroundColor: backrs.colors['Colors/Background/bg-brand-solid-hover'],
    boxShadow: backrs.shadows['shadow-md'],
  },
});
